/** File categories, extensions and type badges for the Files module. */

export type DriveCategory = 'model' | 'gcode' | 'image' | 'document' | 'other'

const MODEL = ['stl', '3mf', 'obj', 'step', 'stp', 'amf', 'ply', 'f3d', 'scad', 'blend', 'fbx', 'glb', 'gltf']
const GCODE = ['gcode', 'bgcode', 'gco', 'g', 'gcode.3mf']
const IMAGE = ['png', 'jpg', 'jpeg', 'webp', 'gif', 'svg', 'bmp', 'avif']
const DOCUMENT = ['pdf', 'txt', 'md', 'doc', 'docx', 'rtf', 'csv', 'json', 'ini', 'xls', 'xlsx', 'odt']

/** Lower-case extension without the dot; `x.gcode.3mf` → `gcode.3mf`. */
export function extOf(name: string): string {
  const lower = name.toLowerCase()
  if (lower.endsWith('.gcode.3mf')) return 'gcode.3mf'
  const i = lower.lastIndexOf('.')
  return i > 0 ? lower.slice(i + 1) : ''
}

export function categoryOf(ext: string): DriveCategory {
  if (GCODE.includes(ext)) return 'gcode'
  if (MODEL.includes(ext)) return 'model'
  if (IMAGE.includes(ext)) return 'image'
  if (DOCUMENT.includes(ext)) return 'document'
  return 'other'
}

export const CATEGORY_LABELS: Record<DriveCategory, string> = {
  model: '3D Models',
  gcode: 'GCode',
  image: 'Images',
  document: 'Documents',
  other: 'Other',
}

/** Short badge text: STL, 3MF, GCODE… */
export function badgeOf(ext: string): string {
  if (ext === 'gcode.3mf') return 'GCODE'
  if (ext === 'gco' || ext === 'g') return 'GCODE'
  return ext ? ext.toUpperCase() : 'FILE'
}

/** Tailwind classes for a file-kind badge. File kinds are plain chips in the
 *  design system: the kind's name does the work, not a color per format. */
export function badgeClass(_ext: string): string {
  return 'bg-elevated text-fg-2 border border-line'
}

/** Whether the browser can show a 3D preview for this extension. */
export function canPreview3d(ext: string): ext is 'stl' | '3mf' | 'obj' | 'step' {
  return ['stl', '3mf', 'obj', 'step', 'stp'].includes(ext)
}

export function formatBytes(bytes: number | null | undefined): string {
  if (!bytes) return '0 B'
  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB']
  const i = Math.min(sizes.length - 1, Math.floor(Math.log(bytes) / Math.log(k)))
  const v = bytes / Math.pow(k, i)
  return `${v >= 100 || i === 0 ? Math.round(v) : v.toFixed(1)} ${sizes[i]}`
}

export function formatDate(iso: string | null | undefined): string {
  if (!iso) return ''
  return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
}

export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return ''
  return new Date(iso).toLocaleString(undefined, { year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
}

/**
 * Slicers that can open a file from a web link through their URL scheme
 * (the same mechanism MakerWorld and Printables use).
 */
export const SLICERS = [
  { id: 'bambu', name: 'Bambu Studio', scheme: 'bambustudio://open?file=' },
  { id: 'orca', name: 'OrcaSlicer', scheme: 'orcaslicer://open?file=' },
  { id: 'prusa', name: 'PrusaSlicer', scheme: 'prusaslicer://open?file=' },
] as const
export type SlicerId = (typeof SLICERS)[number]['id']
